import ProviderAPIClient, { type AddressSuggestion } from '@/features/auth/ProviderAPIClient'
import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useRef, useState } from 'react'

const DEBOUNCE_MS = 250
const MIN_QUERY_LEN = 3

export interface UseAddressAutocompleteResult {
  suggestions: AddressSuggestion[]
  isLoading: boolean
  clear: () => void
}

/**
 * Debounced address autosuggest backed by provider-api's /geo proxy (AWS
 * Location). Each keystroke supersedes the prior request via AbortController,
 * so only the latest query resolves. Errors are swallowed to Sentry — a failing
 * suggestion service must never block manual entry.
 */
export function useAddressAutocomplete(query: string): UseAddressAutocompleteResult {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const clear = useCallback(() => {
    abortRef.current?.abort()
    setSuggestions([])
    setIsLoading(false)
  }, [])

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_LEN) {
      abortRef.current?.abort()
      setSuggestions([])
      setIsLoading(false)
      return
    }

    const handle = setTimeout(() => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setIsLoading(true)

      ProviderAPIClient.getInstance()
        .getAddressSuggestions(trimmed, controller.signal)
        .then((results) => {
          if (!controller.signal.aborted) {
            setSuggestions(results)
          }
        })
        .catch((err: unknown) => {
          // AbortError arrives as a DOMException (name-only check; not all
          // runtimes make DOMException an instanceof Error).
          if ((err as { name?: string })?.name === 'AbortError') {
            return
          }
          setSuggestions([])
          Sentry.captureException(err, { tags: { operation: 'geo.suggestions' } })
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsLoading(false)
          }
        })
    }, DEBOUNCE_MS)

    return () => clearTimeout(handle)
  }, [query])

  useEffect(() => () => abortRef.current?.abort(), [])

  return { suggestions, isLoading, clear }
}
